/**
 * ConsentLogPage.tsx — Consent Records & Audit Trail View.
 *
 * Real consent records table from GET /v1/consents with cursor-based pagination (Next/Prev),
 * search by principalId, filter by status (GRANTED, WITHDRAWN, EXPIRED), CSV export trigger,
 * loading skeletons, empty state illustration, and consent details modal.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useModal } from '../../components/ui/Modal';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { apiFetch, getNetworkErrorMessage, withdrawConsent } from '../../lib/api';
import type { ConsentRecord } from '../../lib/types';
import { STORAGE_KEYS } from '../../lib/constants';

export function ConsentLogPage() {
  const { addToast } = useToast();
  const { openModal } = useModal();

  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search & Filter
  const [searchPrincipalId, setSearchPrincipalId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'active' | 'withdrawn' | 'EXPIRED'>('ALL');

  // Consent Details Modal state
  const [selectedConsent, setSelectedConsent] = useState<ConsentRecord | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const activeAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';

  const fetchConsents = useCallback(
    async (cursor?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (searchPrincipalId.trim()) params.append('principalId', searchPrincipalId.trim());
        if (cursor) params.append('cursor', cursor);
        params.append('limit', '15');

        const queryString = params.toString();
        const data = await apiFetch<{ consents: ConsentRecord[]; nextCursor: string | null }>(
          `/v1/consents${queryString ? `?${queryString}` : ''}`,
          { useApiKey: true },
        );

        setConsents(data.consents || []);
        setNextCursor(data.nextCursor || null);
      } catch (err) {
        setError(getNetworkErrorMessage(err, 'Failed to fetch consent log records.'));
      } finally {
        setIsLoading(false);
      }
    },
    [searchPrincipalId],
  );

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  // Handle Search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCursorHistory([]);
    setCurrentCursorIndex(0);
    fetchConsents();
  };

  // Pagination Next
  const handleNextPage = () => {
    if (!nextCursor) return;
    setCursorHistory((prev) => [...prev, nextCursor]);
    setCurrentCursorIndex((prev) => prev + 1);
    fetchConsents(nextCursor);
  };

  // Pagination Prev
  const handlePrevPage = () => {
    if (currentCursorIndex <= 0) return;
    const prevIndex = currentCursorIndex - 1;
    const prevCursor = cursorHistory[prevIndex - 1] || undefined;
    setCurrentCursorIndex(prevIndex);
    fetchConsents(prevCursor);
  };

  // Filter local consents by status
  const filteredConsents = consents.filter((c) => {
    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'active') return c.status === 'active';
    if (statusFilter === 'withdrawn') return c.status === 'withdrawn';
    if (statusFilter === 'EXPIRED') return c.status === ('expired' as any);
    return true;
  });

  // CSV Export
  const handleExportCSV = () => {
    if (consents.length === 0) return;

    const headers = ['Consent ID', 'Principal Hash', 'Notice Version', 'Purposes', 'Status', 'Created At', 'Updated At'];
    const rows = filteredConsents.map((c) => [
      c.id,
      c.principal_hash,
      c.notice_version.toString(),
      `"${c.purpose_ids}"`,
      c.status,
      new Date(c.created_at * 1000).toISOString(),
      new Date(c.updated_at * 1000).toISOString(),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `consent_log_${activeAppId || 'export'}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    addToast({ type: 'success', message: 'Consent log exported to CSV!' });
  };

  // Withdraw Consent Action
  const handleWithdrawConsent = async (consentId: string) => {
    openModal({
      title: 'Withdraw Consent?',
      description: `This will mark consent record ${consentId} as WITHDRAWN and dispatch webhook updates to downstream targets.`,
      confirmText: 'Withdraw Consent',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        setIsWithdrawing(true);
        try {
          await withdrawConsent(consentId);
          addToast({ type: 'success', message: `Consent ${consentId} withdrawn successfully!` });
          setSelectedConsent(null);
          await fetchConsents();
        } catch (err) {
          addToast({ type: 'error', message: getNetworkErrorMessage(err, 'Failed to withdraw consent.') });
        } finally {
          setIsWithdrawing(false);
        }
      },
    });
  };

  return (
    <div className="space-y-8">
      {/* Header & Export Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Consent Log</h1>
          <p className="mt-1 text-sm text-sub">
            Immutable audit register of all user consent grants and withdrawals.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportCSV}
          disabled={consents.length === 0}
          className="btn-g text-xs font-semibold py-2.5 px-4 disabled:opacity-50 flex items-center gap-2"
        >
          <span>📥</span> Export CSV Log
        </button>
      </div>

      {/* Search & Filter controls */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-4 shadow-card">
        <form onSubmit={handleSearchSubmit} className="flex-1 flex gap-2 max-w-md">
          <input
            type="text"
            placeholder="Search by Principal ID or Hash..."
            value={searchPrincipalId}
            onChange={(e) => setSearchPrincipalId(e.target.value)}
            className="w-full rounded-xl border border-line bg-card px-4 py-2 text-xs text-ink placeholder-muted focus:border-blue-500 outline-none"
          />
          <button type="submit" className="btn-p text-xs py-2 px-4 shrink-0">
            Search
          </button>
        </form>

        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-muted uppercase tracking-wider">Status:</span>
          <div className="flex rounded-xl border border-line bg-card p-1">
            {(['ALL', 'active', 'withdrawn', 'EXPIRED'] as const).map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition ${
                  statusFilter === st
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-sub hover:text-ink hover:bg-slate-500/10'
                }`}
              >
                {st === 'active' ? 'GRANTED' : st.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className="rounded-xl border border-err/30 bg-err/10 p-4 flex items-center justify-between text-sm text-err">
          <span>⚠️ {error}</span>
          <button type="button" onClick={() => fetchConsents()} className="text-xs font-bold underline hover:no-underline ml-4">
            Retry
          </button>
        </div>
      )}

      {/* Consent Table */}
      <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card">
        {isLoading ? (
          <div className="p-6">
            <SkeletonTable rows={5} />
          </div>
        ) : filteredConsents.length === 0 ? (
          <EmptyState
            icon="📝"
            title="No Consent Records Found"
            description={
              searchPrincipalId
                ? `No consent record matched "${searchPrincipalId}".`
                : 'Consents granted by data principals via widget or API will be recorded here.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-ink">
              <thead className="border-b border-line bg-card text-[10px] font-bold uppercase tracking-wider text-muted">
                <tr>
                  <th className="px-6 py-4">Consent ID</th>
                  <th className="px-6 py-4">Principal Hash</th>
                  <th className="px-6 py-4">Notice Ver</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Recorded At</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filteredConsents.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedConsent(c)}
                    className="hover:bg-slate-500/5 transition cursor-pointer"
                  >
                    <td className="px-6 py-4 font-mono font-semibold text-blue-600 dark:text-blue-400">
                      {c.id}
                    </td>
                    <td className="px-6 py-4 font-mono text-muted max-w-xs truncate" title={c.principal_hash}>
                      {c.principal_hash}
                    </td>
                    <td className="px-6 py-4 font-semibold text-sub">v{c.notice_version}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          c.status === 'active'
                            ? 'border border-ok/30 bg-ok/10 text-ok'
                            : 'border border-err/30 bg-err/10 text-err'
                        }`}
                      >
                        {c.status === 'active' ? 'GRANTED' : c.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-muted">
                      {new Date(c.created_at * 1000).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedConsent(c);
                        }}
                        className="text-xs font-semibold text-blue-600 hover:underline"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Cursor Pagination Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-line bg-card text-xs text-sub">
          <span>Page {currentCursorIndex + 1}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentCursorIndex <= 0 || isLoading}
              className="btn-g text-xs py-1.5 px-3 disabled:opacity-40"
            >
              ← Previous
            </button>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={!nextCursor || isLoading}
              className="btn-g text-xs py-1.5 px-3 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {selectedConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl border border-line bg-card p-6 shadow-cardhover space-y-6">
            <div className="flex items-center justify-between border-b border-line pb-4">
              <h3 className="text-lg font-bold text-ink flex items-center gap-2">
                <span>📜</span> Consent Record Details
              </h3>
              <button
                type="button"
                onClick={() => setSelectedConsent(null)}
                className="text-sub hover:text-ink text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <span className="text-muted font-bold uppercase tracking-wider block">Consent ID</span>
                <p className="font-mono text-ink font-semibold mt-0.5">{selectedConsent.id}</p>
              </div>

              <div>
                <span className="text-muted font-bold uppercase tracking-wider block">Principal Hash</span>
                <p className="font-mono text-ink break-all mt-0.5 bg-surface p-2.5 rounded-lg border border-line">
                  {selectedConsent.principal_hash}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-muted font-bold uppercase tracking-wider block">Status</span>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider mt-1 ${
                      selectedConsent.status === 'active'
                        ? 'border border-ok/30 bg-ok/10 text-ok'
                        : 'border border-err/30 bg-err/10 text-err'
                    }`}
                  >
                    {selectedConsent.status === 'active' ? 'GRANTED' : selectedConsent.status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="text-muted font-bold uppercase tracking-wider block">Notice Version</span>
                  <span className="text-ink font-semibold mt-1 block">Version {selectedConsent.notice_version}</span>
                </div>
              </div>

              <div>
                <span className="text-muted font-bold uppercase tracking-wider block">Granted Purposes</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(() => {
                    try {
                      const ids = JSON.parse(selectedConsent.purpose_ids);
                      return Array.isArray(ids)
                        ? ids.map((id: string) => (
                            <span key={id} className="rounded-lg bg-surface border border-line px-2.5 py-1 text-ink font-mono">
                              {id}
                            </span>
                          ))
                        : <span className="text-muted">{selectedConsent.purpose_ids}</span>;
                    } catch {
                      return <span className="text-muted">{selectedConsent.purpose_ids}</span>;
                    }
                  })()}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-line">
                <div>
                  <span className="text-muted block text-[10px]">Created At</span>
                  <span className="text-sub font-medium">{new Date(selectedConsent.created_at * 1000).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted block text-[10px]">Last Updated</span>
                  <span className="text-sub font-medium">{new Date(selectedConsent.updated_at * 1000).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-line flex items-center justify-between gap-3">
              {selectedConsent.status === 'active' ? (
                <button
                  type="button"
                  onClick={() => handleWithdrawConsent(selectedConsent.id)}
                  disabled={isWithdrawing}
                  className="rounded-xl border border-err/30 bg-err/10 px-4 py-2.5 text-xs font-semibold text-err hover:bg-err/20 disabled:opacity-50 flex items-center gap-2"
                >
                  {isWithdrawing ? <Spinner size="sm" /> : 'Withdraw Consent'}
                </button>
              ) : (
                <span className="text-xs text-muted italic">Consent already withdrawn</span>
              )}
              <button
                type="button"
                onClick={() => setSelectedConsent(null)}
                className="btn-g text-xs py-2.5 px-4"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
